
import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Search, Check, X, Pill, AlertCircle } from "lucide-react";
import { useMedicationSearch } from "@/hooks/useMedicationSearch";
import { MedicationSearchResult } from "@/services/medicationApi";

interface MedicationInputProps {
  medications: string[];
  onMedicationsChange: (medications: string[]) => void;
  placeholder?: string;
}

export function MedicationInput({ 
  medications, 
  onMedicationsChange, 
  placeholder = "Search for medications..." 
}: MedicationInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { searchResults, isSearching, searchError, searchMedications, clearSearch } = useMedicationSearch();

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowDropdown(true);
    
    if (value.trim()) {
      searchMedications(value);
    } else {
      clearSearch();
    }
  };

  const addMedication = (medication: string | MedicationSearchResult) => {
    const medicationName = typeof medication === 'string' ? medication : medication.name;
    
    if (medicationName.trim() && !medications.some(med => 
      med.toLowerCase() === medicationName.toLowerCase()
    )) {
      onMedicationsChange([...medications, medicationName.trim()]);
    }
    
    setInputValue("");
    setShowDropdown(false);
    clearSearch();
    inputRef.current?.focus();
  };

  const removeMedication = (medicationToRemove: string) => {
    onMedicationsChange(medications.filter(med => med !== medicationToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there are search results, add the first one
      if (searchResults.length > 0) {
        addMedication(searchResults[0]);
      } else if (inputValue.trim()) {
        // Add as custom medication if no search results
        addMedication(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      clearSearch();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyPress}
            onFocus={() => inputValue && setShowDropdown(true)}
            className="pl-10 pr-20"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            {isSearching && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => inputValue.trim() && addMedication(inputValue.trim())}
              disabled={!inputValue.trim()}
              className="h-8 px-2"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Search Results Dropdown */}
        {showDropdown && (searchResults.length > 0 || searchError || isSearching) && (
          <Card 
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-1 z-50 max-h-64 overflow-y-auto shadow-lg border"
          >
            <CardContent className="p-0">
              {searchError && (
                <div className="flex items-center gap-2 p-3 text-red-600 bg-red-50">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{searchError}</span>
                </div>
              )}
              
              {searchResults.map((result) => (
                <div
                  key={result.rxcui}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  onClick={() => addMedication(result)}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{result.name}</div>
                    {result.tty && (
                      <div className="text-xs text-gray-500">
                        {result.tty === 'BN' ? 'Brand Name' : 
                         result.tty === 'IN' ? 'Ingredient' :
                         result.tty === 'SCD' ? 'Clinical Drug' : 
                         result.tty}
                      </div>
                    )}
                  </div>
                  <Check className="w-4 h-4 text-green-600" />
                </div>
              ))}
              
              {!isSearching && searchResults.length === 0 && inputValue.trim() && (
                <div 
                  className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                  onClick={() => addMedication(inputValue.trim())}
                >
                  <div className="flex items-center gap-2">
                    <Pill className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">Add "{inputValue}" as custom medication</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Selected Medications */}
      {medications.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Current medications:</label>
          <div className="flex flex-wrap gap-2">
            {medications.map((medication, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="flex items-center gap-1 px-3 py-1"
              >
                <Pill className="w-3 h-3" />
                <span>{medication}</span>
                <button
                  type="button"
                  onClick={() => removeMedication(medication)}
                  className="ml-1 hover:text-red-600 transition-colors"
                  aria-label={`Remove ${medication}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        Start typing to search medications from the RxNorm database, or add custom medications.
      </p>
    </div>
  );
}
